import { useCallback, useEffect, useMemo, useState } from "react";
import { isSameDay } from "date-fns";
import { DateRange } from "@common/helpers/date";
import { useConstant } from "@hooks/useConstant";
import {
    DATE_RANGE_PRESETS,
    DATE_RANGE_PRESETS_BY_ID,
    DateRangePreset,
} from "@helpers/date";

type DateSetFrom = "calendar" | "preset" | "input";

interface DateRangePickerOptions {
    initialValue?: Partial<DateRange>;
    onChange: (
        value: Partial<DateRange>,
        dateSetFrom: DateSetFrom | undefined,
        isAllTime?: boolean,
    ) => void;
    requireSelection?: boolean;
    visiblePresets?: string[];
    pinnedPresets?: readonly string[];
    maxDate?: Date;
}

export function useDateRangePicker({
    initialValue = {},
    requireSelection = false,
    onChange,
    visiblePresets,
    maxDate,
}: DateRangePickerOptions) {
    const today = useConstant(() => new Date());
    const max = maxDate ?? today;

    const [start, setStart] = useState(initialValue.start);
    const [end, setEnd] = useState(initialValue.end);
    const [dateSetFrom, setDateSetFrom] = useState<DateSetFrom>();

    // Display order follows the caller, not `DATE_RANGE_PRESETS` (match-precedence order).
    const presets = useMemo(
        () =>
            visiblePresets
                ? visiblePresets
                      .map((id) => DATE_RANGE_PRESETS_BY_ID[id])
                      .filter((p): p is DateRangePreset => Boolean(p))
                : DATE_RANGE_PRESETS,
        [visiblePresets],
    );

    useEffect(() => {
        if (!requireSelection || (start && end)) {
            onChange({ start, end }, dateSetFrom, !start && !end);
        }
    }, [start, end, onChange, dateSetFrom, requireSelection]);

    const clearValue = useCallback(() => {
        setStart(undefined);
        setEnd(undefined);
        setDateSetFrom("preset");
    }, []);

    const selectPreset = useCallback(
        (preset?: DateRangePreset) => {
            if (preset) {
                const presetStart = preset.getStart(today);

                setStart(presetStart);
                setEnd(preset.getEnd(today));
                setDateSetFrom("preset");
            } else {
                clearValue();
            }
        },
        [clearValue, today],
    );

    const activePreset = useMemo(() => {
        if (start && end) {
            return DATE_RANGE_PRESETS.find(
                (p) =>
                    isSameDay(p.getStart(today), start) &&
                    isSameDay(p.getEnd(today), end),
            )?.id;
        }
    }, [start, end, today]);

    return {
        start,
        setStart,
        end,
        setEnd,
        maxDate: max,
        activePreset,
        presets,
        selectPreset,
    };
}
